import { create } from 'zustand';
import type {
  Offer,
  OfferStatus,
  BrandStackingSettings,
  LocationGroup,
  PromoCode,
  OfferLocationOverride,
} from '../data/types';

// Valid status transitions
const VALID_TRANSITIONS: Record<OfferStatus, OfferStatus[]> = {
  draft: ['active'],
  active: ['paused', 'expired', 'archived'],
  paused: ['active', 'archived'],
  expired: ['archived'],
  scheduled: ['active', 'archived'],
  archived: ['draft'],
};

interface OffersStore {
  offers: Offer[];
  brandStackingSettings: BrandStackingSettings;
  locationGroups: LocationGroup[];

  // CRUD
  setOffers: (offers: Offer[]) => void;
  addOffer: (offer: Omit<Offer, 'id' | 'createdAt' | 'updatedAt' | 'version' | 'redemptionCount'>) => void;
  updateOffer: (id: string, updates: Partial<Offer>) => void;
  deleteOffer: (id: string) => void;
  archiveOffer: (id: string) => void;
  duplicateOffer: (id: string) => void;
  transitionStatus: (id: string, newStatus: OfferStatus) => boolean;

  // Promo Codes
  addPromoCode: (offerId: string, code: Omit<PromoCode, 'id' | 'offerId' | 'usageCount' | 'createdAt'>) => void;
  updatePromoCode: (offerId: string, codeId: string, updates: Partial<PromoCode>) => void;
  deactivatePromoCode: (offerId: string, codeId: string) => void;

  // Stacking
  setBrandStackingSettings: (settings: BrandStackingSettings) => void;

  // Location Groups
  setLocationGroups: (groups: LocationGroup[]) => void;
  addLocationGroup: (group: Omit<LocationGroup, 'id'>) => void;
  updateLocationGroup: (id: string, updates: Partial<LocationGroup>) => void;
  removeLocationGroup: (id: string) => void;

  // Location Overrides
  addLocationOverride: (offerId: string, override: OfferLocationOverride) => void;
  updateLocationOverride: (offerId: string, locationId: string, updates: Partial<OfferLocationOverride>) => void;
  removeLocationOverride: (offerId: string, locationId: string) => void;

  // Bulk
  bulkAssignLocations: (offerIds: string[], locationIds: string[]) => void;

  // Selectors
  getOfferById: (id: string) => Offer | undefined;
  getOffersByStatus: (status: OfferStatus) => Offer[];
  getPromoCodeByCode: (code: string) => { offer: Offer; promoCode: PromoCode } | undefined;
}

export const useOffers = create<OffersStore>((set, get) => ({
  offers: [],
  brandStackingSettings: { policy: 'none', maxStackCount: null },
  locationGroups: [],

  // ── CRUD ──

  setOffers: (offers) => set({ offers }),

  addOffer: (offer) =>
    set((state) => ({
      offers: [
        ...state.offers,
        {
          ...offer,
          id: crypto.randomUUID(),
          redemptionCount: 0,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          version: 1,
        },
      ],
    })),

  updateOffer: (id, updates) =>
    set((state) => ({
      offers: state.offers.map((o) =>
        o.id === id
          ? { ...o, ...updates, updatedAt: new Date().toISOString(), version: o.version + 1 }
          : o
      ),
    })),

  deleteOffer: (id) =>
    set((state) => ({
      offers: state.offers.filter((o) => o.id !== id),
    })),

  archiveOffer: (id) =>
    set((state) => ({
      offers: state.offers.map((o) =>
        o.id === id
          ? {
              ...o,
              status: 'archived' as const,
              // Deactivate all linked promo codes when archiving (1.8 E2)
              promoCodes: o.promoCodes.map((c) => ({ ...c, status: 'inactive' as const })),
              updatedAt: new Date().toISOString(),
              version: o.version + 1,
            }
          : o
      ),
    })),

  duplicateOffer: (id) => {
    const original = get().offers.find((o) => o.id === id);
    if (!original) return;
    set((state) => ({
      offers: [
        ...state.offers,
        {
          ...original,
          id: crypto.randomUUID(),
          name: `${original.name}`,
          status: 'draft' as const,
          promoCodes: [], // promo codes are unique per offer
          redemptionCount: 0,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          version: 1,
        },
      ],
    }));
  },

  transitionStatus: (id, newStatus) => {
    const offer = get().offers.find((o) => o.id === id);
    if (!offer) return false;
    const allowed = VALID_TRANSITIONS[offer.status];
    if (!allowed?.includes(newStatus)) return false;
    set((state) => ({
      offers: state.offers.map((o) =>
        o.id === id
          ? { ...o, status: newStatus, updatedAt: new Date().toISOString(), version: o.version + 1 }
          : o
      ),
    }));
    return true;
  },

  // ── Promo Codes ──

  addPromoCode: (offerId, code) =>
    set((state) => ({
      offers: state.offers.map((o) =>
        o.id === offerId
          ? {
              ...o,
              promoCodes: [
                ...o.promoCodes,
                {
                  ...code,
                  id: crypto.randomUUID(),
                  offerId,
                  usageCount: 0,
                  createdAt: new Date().toISOString(),
                },
              ],
              updatedAt: new Date().toISOString(),
            }
          : o
      ),
    })),

  updatePromoCode: (offerId, codeId, updates) =>
    set((state) => ({
      offers: state.offers.map((o) =>
        o.id === offerId
          ? {
              ...o,
              promoCodes: o.promoCodes.map((c) =>
                c.id === codeId ? { ...c, ...updates } : c
              ),
              updatedAt: new Date().toISOString(),
            }
          : o
      ),
    })),

  deactivatePromoCode: (offerId, codeId) =>
    set((state) => ({
      offers: state.offers.map((o) =>
        o.id === offerId
          ? {
              ...o,
              promoCodes: o.promoCodes.map((c) =>
                c.id === codeId ? { ...c, status: 'inactive' as const } : c
              ),
              updatedAt: new Date().toISOString(),
            }
          : o
      ),
    })),

  // ── Stacking ──

  setBrandStackingSettings: (settings) => set({ brandStackingSettings: settings }),

  // ── Location Groups ──

  setLocationGroups: (groups) => set({ locationGroups: groups }),

  addLocationGroup: (group) =>
    set((state) => ({
      locationGroups: [...state.locationGroups, { ...group, id: crypto.randomUUID() }],
    })),

  updateLocationGroup: (id, updates) =>
    set((state) => ({
      locationGroups: state.locationGroups.map((g) =>
        g.id === id ? { ...g, ...updates } : g
      ),
    })),

  removeLocationGroup: (id) =>
    set((state) => ({
      locationGroups: state.locationGroups.filter((g) => g.id !== id),
    })),

  // ── Location Overrides ──

  addLocationOverride: (offerId, override) =>
    set((state) => ({
      offers: state.offers.map((o) =>
        o.id === offerId
          ? { ...o, locationOverrides: [...o.locationOverrides, override], updatedAt: new Date().toISOString() }
          : o
      ),
    })),

  updateLocationOverride: (offerId, locationId, updates) =>
    set((state) => ({
      offers: state.offers.map((o) =>
        o.id === offerId
          ? {
              ...o,
              locationOverrides: o.locationOverrides.map((ov) =>
                ov.locationId === locationId ? { ...ov, ...updates } : ov
              ),
              updatedAt: new Date().toISOString(),
            }
          : o
      ),
    })),

  removeLocationOverride: (offerId, locationId) =>
    set((state) => ({
      offers: state.offers.map((o) =>
        o.id === offerId
          ? {
              ...o,
              locationOverrides: o.locationOverrides.filter((ov) => ov.locationId !== locationId),
              updatedAt: new Date().toISOString(),
            }
          : o
      ),
    })),

  // ── Bulk ──

  bulkAssignLocations: (offerIds, locationIds) =>
    set((state) => ({
      offers: state.offers.map((o) =>
        offerIds.includes(o.id)
          ? {
              ...o,
              locationScope: 'specific' as const,
              locationIds: Array.from(new Set([...o.locationIds, ...locationIds])),
              updatedAt: new Date().toISOString(),
            }
          : o
      ),
    })),

  // ── Selectors ──

  getOfferById: (id) => get().offers.find((o) => o.id === id),

  getOffersByStatus: (status) => get().offers.filter((o) => o.status === status),

  getPromoCodeByCode: (code) => {
    const upper = code.toUpperCase();
    for (const offer of get().offers) {
      const found = offer.promoCodes.find((c) => c.code.toUpperCase() === upper);
      if (found) return { offer, promoCode: found };
    }
    return undefined;
  },
}));
